import RandomizeIcon from '@atlaskit/icon-lab/core/randomize';
import PageIcon from '@atlaskit/icon/core/page';
import {
	BUILTIN_TOOLBAR_KEYS,
	createEditorToolbarActions,
	type NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

export const PAGE_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('page');

const isPageUrl = (url: string): boolean => /\/wiki\/spaces\/[^/]+\/(pages|blog)\/\d+/.test(url);

const isEditEmbedUrl = (url: string): boolean =>
	/\/wiki\/spaces\/[^/]+\/(pages|blog)\/edit-embed\/\d+/.test(url);

/**
 * Experience manifest for a page embed.
 * Register with getNativeEmbedFacade().registerExperienceManifest(PageExperienceManifest),
 * then call getNativeEmbedFacade().createExperienceForUrl(url) to obtain an Experience for EmbedFrameUI.
 */
export const PageExperienceManifest = {
	enforceQueryParams: true,
	id: PAGE_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {},
	isUrlMatch: isPageUrl,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	isActive: () => true,
	shouldAutoConvertToNativeEmbed: true,
	transformUrl: (url: string, _isFullScreen?: boolean): string => {
		const urlObj = new URL(url);
		urlObj.searchParams.set('sample-param', 'true');
		return urlObj.toString();
	},
	uiConfig: {
		showHeader: (meta, url) =>
			!(meta.documentType === 'schema:DigitalDocument' && isEditEmbedUrl(url)),
		headerControls: {
			editButton: {
				show: (meta) => meta.documentType !== 'schema:DigitalDocument', // Don't show edit button for live docs
				onSwitchToEditing: (currentUrl: string) => {
					const url = new URL(currentUrl);
					// Change /pages/<id> or /blog/<id> to /pages/edit-embed/<id> or /blog/edit-embed/<id>
					url.pathname = url.pathname.replace(/\/(pages|blog)\/(\d+)/, '/$1/edit-embed/$2');
					url.searchParams.set('initialAllowedFeatures', 'delete-draft');
					return url.toString();
				},
				onSwitchToViewing: (currentUrl: string) => {
					const url = new URL(currentUrl);
					// Change /pages/edit-embed/<id> or /blog/edit-embed/<id> back to /pages/<id> or /blog/<id>
					url.pathname = url.pathname.replace(/\/(pages|blog)\/edit-embed\/(\d+)/, '/$1/$2');
					url.searchParams.delete('initialAllowedFeatures');
					return url.toString();
				},
			},
		},
		editorToolbarActions: createEditorToolbarActions({
			customActions: {
				remix: {
					type: 'button',
					key: 'Remix',
					handlerKey: 'remix',
					// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
					label: 'Remix',
					icon: RandomizeIcon,
					showTitle: true,
				},
			},
			items: [
				BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
				'remix',
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.EMBED,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
				BUILTIN_TOOLBAR_KEYS.EDIT_URL,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
			],
			moreItems: [
				BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
				BUILTIN_TOOLBAR_KEYS.COPY_LINK,
				BUILTIN_TOOLBAR_KEYS.DELETE,
			],
		}),
		editorToolbarHandlers: {
			onAskRovoClick: (parameters: NativeEmbedParameterValues) => {
				if (parameters.url) {
					const url = new URL(parameters.url);
					url.searchParams.set('rovoChatTriggerOpen', 'true');
					window.open(url.toString(), '_blank', 'noopener,noreferrer');
				}
			},
		},
		titleIcon: PageIcon,
		appearance: 'overlay',
	},
} as const satisfies ExperienceManifest;
